/**
 * Share: seventeen sweets between five children.
 *
 * The Divide toy asks how many groups of something fit inside a pile. This
 * asks the question a child actually asks, and deals the things out one at a
 * time round the table so the answer arrives at the speed of the dealing. The
 * two that will not go round again sit in the middle, and that is a remainder:
 * not a leftover number, but two sweets nobody can have.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Hand, Home, RotateCcw } from 'lucide-preact';
import type { Vec } from '../../engine/vec.ts';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import type { Character } from '../../engine/character.ts';
import { bobOffset, characterFrom, drawCharacter } from '../../engine/character.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { neededToLevel, plateOf, shareLine, shareOut } from '../../lib/sharing.ts';
import { Coach, CrayonRow, Stepper, ToyButton } from '../../ui/controls.tsx';
import { NumberPad } from '../../ui/NumberPad.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const MAX_THINGS = 60;
const MAX_PLATES = 10;
const TOP = 150;
const DEAL_INTERVAL = 90;

interface Thing {
  pos: Vec;
  target: Vec;
  home: Vec;
}

export function ShareToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const thingsRef = useRef<Thing[]>([]);
  const sparksRef = useRef<Spark[]>([]);
  const layoutRef = useRef({ plateY: 0, plateR: 40, dotR: 9 });
  const clockRef = useRef(0);
  const facesRef = useRef(new Map<number, Character>());

  /** Thing number 3 is always the same somebody, whatever colour it turns. */
  const faceFor = (index: number, color: string): Character => {
    const existing = facesRef.current.get(index);
    if (existing && existing.color === color) return existing;
    const made = characterFrom(index * 40503 + 11, color);
    facesRef.current.set(index, made);
    return made;
  };

  const [total, setTotal] = useState(17);
  const [plates, setPlates] = useState(5);
  const [dealt, setDealt] = useState(0);
  const [color, setColor] = useState(() => token('--crayon-red'));
  const [editing, setEditing] = useState<'total' | 'plates' | null>(null);

  const share = shareOut(total, plates);
  const live = useRef({ share, dealt, color });
  live.current = { share, dealt, color };

  const finished = dealt >= total;

  // Deal one at a time, on a beat, so the sharing is watched rather than shown.
  useEffect(() => {
    if (dealt >= total) return;
    const timer = setTimeout(() => {
      setDealt((d) => Math.min(total, d + 1));
      play('count', Math.min(1, ((dealt % plates) + 1) / plates));
    }, DEAL_INTERVAL);
    return () => clearTimeout(timer);
  }, [dealt, total, plates]);

  // Celebrate only when it goes round exactly — that is the interesting case.
  const wasFinished = useRef(finished);
  useEffect(() => {
    if (finished && !wasFinished.current && share.left === 0 && total > 0) {
      play('yay', 0.8);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) spawnSparks(sparksRef.current, { x: rect.width / 2, y: rect.height * 0.55 }, partyColors(), 26, 420);
    }
    wasFinished.current = finished;
  }, [finished, share.left, total]);

  const restart = (nextTotal = total, nextPlates = plates) => {
    setTotal(nextTotal);
    setPlates(nextPlates);
    setDealt(0);
    thingsRef.current = [];
  };

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      updateSparks(sparksRef.current, dt);
      clockRef.current += dt;
      const { share: s, dealt: given, color: c } = live.current;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const plateR = Math.min(52, (width - 24) / (s.plates * 2.25));
      const plateY = height * 0.62;
      // Big enough that a face is legible, small enough that sixty still fit.
      const dotR = Math.max(6, Math.min(12, plateR * 0.24));
      layoutRef.current = { plateY, plateR, dotR };

      const spread = width / s.plates;
      const plateX = (i: number) => spread * (i + 0.5);

      // The plates.
      for (let i = 0; i < s.plates; i++) {
        ctx.save();
        ctx.strokeStyle = token('--ink-faint');
        ctx.fillStyle = token('--paper-deep');
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(plateX(i), plateY, plateR, plateR * 0.42, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }

      // Keep one dot per thing, springing towards wherever it belongs now.
      while (thingsRef.current.length < s.total) {
        const start = { x: width / 2 + (Math.random() - 0.5) * 60, y: TOP + 30 };
        thingsRef.current.push({ pos: { ...start }, target: { ...start }, home: { ...start } });
      }
      thingsRef.current.length = s.total;

      const leftoverTop = plateY + plateR * 0.42 + 46;
      thingsRef.current.forEach((thing, i) => {
        if (i >= given) {
          // Still waiting in the pile at the top.
          const perRow = Math.max(1, Math.floor((width - 40) / (dotR * 2.6)));
          const waiting = i - given;
          thing.target = {
            x: width / 2 + ((waiting % perRow) - Math.min(s.total - given, perRow) / 2 + 0.5) * dotR * 2.6,
            y: TOP + 26 + Math.floor(waiting / perRow) * dotR * 2.4,
          };
        } else {
          const plate = plateOf(i, s);
          if (plate === null) {
            // A leftover: it sits below, apart, where it plainly did not fit.
            const k = i - s.each * s.plates;
            thing.target = {
              x: width / 2 + (k - (s.left - 1) / 2) * dotR * 3,
              y: leftoverTop,
            };
          } else {
            const lap = Math.floor(i / s.plates);
            const perRow = 3;
            thing.target = {
              x: plateX(plate) + ((lap % perRow) - 1) * dotR * 2.3,
              y: plateY - dotR - Math.floor(lap / perRow) * dotR * 2.1,
            };
          }
        }
        const k = 1 - Math.exp(-dt * 13);
        thing.pos.x += (thing.target.x - thing.pos.x) * k;
        thing.pos.y += (thing.target.y - thing.pos.y) * k;
      });

      // Draw the leftovers' shelf first, so it reads as a place, not a mistake.
      if (s.left > 0 && given >= s.total) {
        ctx.save();
        ctx.fillStyle = token('--ink-soft');
        ctx.textAlign = 'center';
        ctx.font = `700 14px ui-rounded, system-ui, sans-serif`;
        // Above the leftovers, not below: the coach line sits along the bottom
        // of the stage and was landing straight on top of this.
        ctx.fillText(`${s.left} left over`, width / 2, leftoverTop - dotR * 2.2);
        ctx.restore();
      }

      // Each thing is a somebody. Sharing seventeen of them between five
      // plates only feels like it matters if they are not anonymous dots —
      // and the two who could not go round look it.
      thingsRef.current.forEach((thing, i) => {
        const waiting = i >= given;
        const leftover = !waiting && plateOf(i, s) === null;
        const ch = faceFor(i, waiting ? token('--ink-faint') : leftover ? token('--crayon-orange') : c);
        drawCharacter(ctx, ch, {
          x: thing.pos.x,
          y: thing.pos.y + bobOffset(ch, clockRef.current, dotR),
          size: dotR,
          t: clockRef.current,
          mood: waiting ? 'sleepy' : leftover ? 'surprised' : 'happy',
        });
      });

      // How many each plate has, written under it once the dealing is done.
      if (given >= s.total) {
        ctx.save();
        ctx.fillStyle = token('--ink');
        ctx.textAlign = 'center';
        ctx.font = `800 ${Math.min(19, plateR * 0.5)}px ui-rounded, system-ui, sans-serif`;
        for (let i = 0; i < s.plates; i++) {
          ctx.fillText(String(s.each), plateX(i), plateY + plateR * 0.42 + 22);
        }
        ctx.restore();
      }

      drawSparks(ctx, sparksRef.current);
    },
  });

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Share things out" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton
              round
              label="Deal them out again"
              hideLabel
              onClick={() => {
                setDealt(0);
                play('whoosh', 0.6);
              }}
            >
              <Hand size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Start again" hideLabel onClick={() => restart(17, 5)}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>
            {share.total} ÷ {share.plates} = {share.each}
          </strong>
          <span>{share.left > 0 ? `and ${share.left} left over` : 'exactly'}</span>
        </p>

        <Coach>
          {finished && share.left > 0
            ? `${neededToLevel(share)} more would share out evenly`
            : shareLine(share)}
        </Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row">
          <Stepper
            value={total}
            min={0}
            max={MAX_THINGS}
            label="things"
            onChange={(n) => restart(n, plates)}
            onPick={() => setEditing('total')}
          />
          <div class="dock__spacer" />
          <Stepper
            value={plates}
            min={1}
            max={MAX_PLATES}
            label="plates"
            onChange={(n) => restart(total, n)}
            onPick={() => setEditing('plates')}
          />
        </div>
        <div class="dock__row">
          <CrayonRow value={color} onChange={setColor} />
        </div>
      </div>

      {editing && (
        <NumberPad
          title={editing === 'total' ? 'How many things?' : 'How many plates?'}
          value={editing === 'total' ? total : plates}
          min={editing === 'total' ? 0 : 1}
          max={editing === 'total' ? MAX_THINGS : MAX_PLATES}
          onDone={(value) => {
            if (editing === 'total') restart(value, plates);
            else restart(total, value);
            setEditing(null);
          }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}
